<template>
  <!-- 中秋国庆营业确认弹窗 -->
  <div class="holiday-mask" v-if="visible" @click.self="handleClose">
    <div class="holiday-wrap">
      <div class="holiday-sheet">
        <!-- 弹窗头部 -->
        <div class="holiday-head">
          <div class="holiday-title">{{ popupTitle }}</div>
          <div class="holiday-desc">为保障消费者体验，请确认您门店在{{ descText }}的营业安排</div>
        </div>

        <!-- 弹窗内容 -->
        <div class="holiday-body">
          <!-- 中秋节（上）：9月24日23:59:59前展示，过期后隐藏 -->
          <div class="holiday-block" v-if="showMidAutumn">
            <div class="block-label">
              <strong>中秋节</strong>
              <span>{{ holidayConfig.midAutumn.dateRange }}</span>
            </div>
            <div class="radio-row">
              <label class="radio-item">
                <input type="radio" v-model="midStatus" value="open" /> 正常营业
              </label>
              <label class="radio-item">
                <input type="radio" v-model="midStatus" value="partial" /> 部分日期休息
              </label>
              <label class="radio-item">
                <input type="radio" v-model="midStatus" value="full" /> 全程休息（{{ holidayConfig.midAutumn.fullLabel }}）
              </label>
            </div>
            <!-- 日期选择面板 -->
            <div class="date-panel" v-if="midStatus === 'partial' || midStatus === 'full'">
              <div class="date-grid">
                <div
                  v-for="item in holidayConfig.midAutumn.dates"
                  :key="item.date"
                  :class="[
                    'date-chip',
                    { 'is-selected': isDateSelected('mid', item.date) },
                    { 'is-disabled': midStatus === 'full' }
                  ]"
                  @click="toggleDate('mid', item.date)"
                >
                  {{ item.label }}<small>{{ item.weekday }}</small>
                </div>
              </div>
              <div :class="['selected-summary', { 'is-warn': midStatus === 'partial' && selectedMidDates.length === 0 }]">
                {{ midSummaryText }}
              </div>
            </div>
          </div>

          <!-- 国庆节（下） -->
          <div class="holiday-block">
            <div class="block-label">
              <strong>国庆节</strong>
              <span>{{ holidayConfig.national.dateRange }}</span>
            </div>
            <div class="radio-row">
              <label class="radio-item">
                <input type="radio" v-model="nationalStatus" value="open" /> 正常营业
              </label>
              <label class="radio-item">
                <input type="radio" v-model="nationalStatus" value="partial" /> 部分日期休息
              </label>
              <label class="radio-item">
                <input type="radio" v-model="nationalStatus" value="full" /> 全程休息（{{ holidayConfig.national.fullLabel }}）
              </label>
            </div>
            <!-- 日期选择面板 -->
            <div class="date-panel" v-if="nationalStatus === 'partial' || nationalStatus === 'full'">
              <div class="date-grid">
                <div
                  v-for="item in holidayConfig.national.dates"
                  :key="item.date"
                  :class="[
                    'date-chip',
                    { 'is-selected': isDateSelected('national', item.date) },
                    { 'is-disabled': nationalStatus === 'full' }
                  ]"
                  @click="toggleDate('national', item.date)"
                >
                  {{ item.label }}<small>{{ item.weekday }}</small>
                </div>
              </div>
              <div :class="['selected-summary', { 'is-warn': nationalStatus === 'partial' && selectedNationalDates.length === 0 }]">
                {{ nationalSummaryText }}
              </div>
            </div>
          </div>
        </div>

        <!-- 提交按钮 -->
        <div class="holiday-foot">
          <van-button round type="info" class="btn-primary" @click="handleSubmit">确认提交</van-button>
        </div>
      </div>

      <!-- 关闭按钮 -->
      <button type="button" class="holiday-close" @click="handleClose">
        <svg viewBox="0 0 24 24" fill="none">
          <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
        </svg>
      </button>
    </div>
  </div>
</template>

<script>
import config from '@/configs/config'

/**
 * 中秋国庆营业确认弹窗组件
 * 用于收集商户在双节期间的营业安排
 */
/** 中秋节模块过期截止时间：2026-09-24 23:59:59 */
const MID_AUTUMN_DEADLINE = new Date('2026-09-24T23:59:59').getTime()
/** 整体下线截止时间：2026-09-30 23:59:59 */
const OVERALL_DEADLINE = new Date('2026-09-30T23:59:59').getTime()

export default {
  name: 'holidayConfirm',
  props: {
    /** 是否显示弹窗 */
    visible: {
      type: Boolean,
      default: false
    },
    /** 当前门店ID */
    shopId: {
      type: [String, Number],
      default: ''
    }
  },
  data() {
    return {
      /** 中秋节营业状态：''-未选择 open-正常营业 partial-部分休息 full-全程休息，默认不选 */
      midStatus: '',
      /** 国庆节营业状态，默认不选 */
      nationalStatus: '',
      /** 中秋节已选休息日期 */
      selectedMidDates: [],
      /** 国庆节已选休息日期 */
      selectedNationalDates: [],
      /** 节假日配置数据 */
      holidayConfig: {
        midAutumn: {
          dateRange: '9月25日 — 9月27日',
          fullLabel: '9/25—9/27',
          dates: [
            { date: '2026-09-25', label: '9/25', weekday: '周五' },
            { date: '2026-09-26', label: '9/26', weekday: '周六' },
            { date: '2026-09-27', label: '9/27', weekday: '周日' }
          ]
        },
        national: {
          dateRange: '10月1日 — 10月7日',
          fullLabel: '10/1—10/7',
          dates: [
            { date: '2026-10-01', label: '10/1', weekday: '周四' },
            { date: '2026-10-02', label: '10/2', weekday: '周五' },
            { date: '2026-10-03', label: '10/3', weekday: '周六' },
            { date: '2026-10-04', label: '10/4', weekday: '周日' },
            { date: '2026-10-05', label: '10/5', weekday: '周一' },
            { date: '2026-10-06', label: '10/6', weekday: '周二' },
            { date: '2026-10-07', label: '10/7', weekday: '周三' }
          ]
        }
      }
    }
  },
  computed: {
    /**
     * 是否展示中秋节模块
     * 9月24日23:59:59后隐藏中秋部分
     * @returns {boolean}
     */
    showMidAutumn() {
      return Date.now() <= MID_AUTUMN_DEADLINE
    },
    /**
     * 弹窗标题（根据中秋模块是否过期动态变化）
     * @returns {string}
     */
    popupTitle() {
      return this.showMidAutumn ? '中秋 · 国庆营业确认' : '国庆营业确认'
    },
    /**
     * 弹窗描述文案（根据中秋模块是否过期动态变化）
     * @returns {string}
     */
    descText() {
      return this.showMidAutumn ? '中秋国庆期间' : '国庆期间'
    },
    /**
     * 中秋节摘要文本
     * @returns {string} 已选日期或提示文案
     */
    midSummaryText() {
      if (this.midStatus === 'full') {
        return '全程休息：9/25 — 9/27'
      }
      if (this.selectedMidDates.length === 0) {
        return '请至少选择 1 个休息日期'
      }
      const labels = this.getLabelsByDates('mid', this.selectedMidDates)
      return '已选休息：' + labels.join('、')
    },
    /**
     * 国庆节摘要文本
     * @returns {string} 已选日期或提示文案
     */
    nationalSummaryText() {
      if (this.nationalStatus === 'full') {
        return '全程休息：10/1 — 10/7'
      }
      if (this.selectedNationalDates.length === 0) {
        return '请至少选择 1 个休息日期'
      }
      const labels = this.getLabelsByDates('national', this.selectedNationalDates)
      return '已选休息：' + labels.join('、')
    }
  },
  watch: {
    /**
     * 监听中秋节状态变化
     * 切换到全程休息时自动选中所有日期
     * 切换到正常营业或部分休息时清空已选日期
     */
    midStatus(val) {
      if (val === 'full') {
        this.selectedMidDates = this.holidayConfig.midAutumn.dates.map(d => d.date)
      } else {
        this.selectedMidDates = []
      }
    },
    /**
     * 监听国庆节状态变化
     * 切换到全程休息时自动选中所有日期
     * 切换到正常营业或部分休息时清空已选日期
     */
    nationalStatus(val) {
      if (val === 'full') {
        this.selectedNationalDates = this.holidayConfig.national.dates.map(d => d.date)
      } else {
        this.selectedNationalDates = []
      }
    }
  },
  methods: {
    /**
     * 判断日期是否被选中
     * @param {string} type - 节日类型：mid 或 national
     * @param {string} date - 日期字符串，如 2026-09-25
     * @returns {boolean} 是否选中
     */
    isDateSelected(type, date) {
      if (type === 'mid') {
        return this.selectedMidDates.includes(date)
      }
      return this.selectedNationalDates.includes(date)
    },

    /**
     * 切换日期选中状态
     * 全程休息模式下不可操作
     * @param {string} type - 节日类型：mid 或 national
     * @param {string} date - 日期字符串
     */
    toggleDate(type, date) {
      // 全程休息模式下禁止手动切换
      if (type === 'mid' && this.midStatus === 'full') return
      if (type === 'national' && this.nationalStatus === 'full') return

      if (type === 'mid') {
        const idx = this.selectedMidDates.indexOf(date)
        if (idx > -1) {
          this.selectedMidDates.splice(idx, 1)
        } else {
          this.selectedMidDates.push(date)
        }
      } else {
        const idx = this.selectedNationalDates.indexOf(date)
        if (idx > -1) {
          this.selectedNationalDates.splice(idx, 1)
        } else {
          this.selectedNationalDates.push(date)
        }
      }
    },

    /**
     * 根据日期数组获取对应的显示标签
     * @param {string} type - 节日类型
     * @param {Array} dates - 日期数组
     * @returns {Array} 标签数组，如 ['9/25', '9/26']
     */
    getLabelsByDates(type, dates) {
      const config = type === 'mid' ? this.holidayConfig.midAutumn : this.holidayConfig.national
      return config.dates.filter(d => dates.includes(d.date)).map(d => d.label)
    },

    /**
     * 判断弹窗是否应该展示（供父组件调用）
     * 整体下线时间：2026-09-30 23:59:59
     * @returns {boolean} 是否应展示
     */
    isWithinDeadline() {
      return Date.now() <= OVERALL_DEADLINE
    },

    /**
     * 确认提交
     * 将中秋和国庆选中的休息日期转换为接口所需格式，提交到 /shop/restTime/save
     */
    async handleSubmit() {
      // 校验：未过期的模块必须选择状态，部分休息必须选日期
      if (this.showMidAutumn && !this.midStatus) {
        return tuhu.toast('请完善营业信息')
      }
      if (!this.nationalStatus) {
        return tuhu.toast('请完善营业信息')
      }
      if (this.showMidAutumn && this.midStatus === 'partial' && this.selectedMidDates.length === 0) {
        return tuhu.toast('请完善营业信息')
      }
      if (this.nationalStatus === 'partial' && this.selectedNationalDates.length === 0) {
        return tuhu.toast('请完善营业信息')
      }

      // 构建 restTimeList：每个选中日期转换为 { restRemark, restStartTime, restEndTime }
      const restTimeList = []
      // 中秋日期（未过期时才添加）
      if (this.showMidAutumn) {
        this.selectedMidDates.forEach(date => {
          restTimeList.push({
            restRemark: '中秋',
            restStartTime: date,
            restEndTime: date
          })
        })
      }
      // 国庆日期
      this.selectedNationalDates.forEach(date => {
        restTimeList.push({
          restRemark: '国庆',
          restStartTime: date,
          restEndTime: date
        })
      })

      // 构建请求参数
      const params = {
        shopId: this.shopId,
        type: 4,
        restTimeList
      }

      try {
        const data = await tuhu.fetch(config.apis.SAVE_REST_TIME, {
          method: 'post',
          body: JSON.stringify(params),
          headers: {
            'Content-Type': 'application/json'
          }
        })
        if (data.code === '200') {
          tuhu.toast('提交成功，感谢您的配合')
        } else {
          tuhu.toast(data.message || '提交失败')
        }
      } catch (e) {
        console.log('saveRestTime err', e)
        tuhu.toast('网络开小差了，请重试')
      }
      // 无论接口成功与否都通知父组件关闭弹窗，触发下一个
      // 延时 500ms 让用户看到 toast 提示后再关闭
      setTimeout(() => {
        this.$emit('close')
      }, 500)
    },

    /**
     * 关闭弹窗
     * 不做提交，仅关闭，由父组件的 noticeIds 机制控制下次是否再弹
     */
    handleClose() {
      this.$emit('close')
    }
  }
}
</script>

<style lang="less" scoped>
/** 遮罩层 */
.holiday-mask {
  position: fixed;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: 3000;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.4rem;
}

/** 弹窗容器 */
.holiday-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 16.5rem;
}

/** 弹窗主体卡片 */
.holiday-sheet {
  width: 100%;
  max-height: 82vh;
  overflow: auto;
  background: #fff;
  border-radius: 0.65rem;
}

/** 弹窗头部 */
.holiday-head {
  padding: 0.9rem 0.85rem 0.5rem;
  border-bottom: 1px solid #f0f0f0;
}

.holiday-title {
  font-size: 0.85rem;
  font-weight: 600;
  color: #1a1a1a;
}

.holiday-desc {
  margin-top: 0.35rem;
  font-size: 0.62rem;
  color: #888;
  line-height: 1.55;
}

/** 弹窗内容区 */
.holiday-body {
  padding: 0.65rem 0.85rem 0.75rem;
}

/** 节日区块 */
.holiday-block {
  margin-bottom: 0.75rem;
  padding: 0.55rem 0.6rem;
  background: #f8fafc;
  border-radius: 0.45rem;
  border: 1px solid #eef2f6;

  &:last-child {
    margin-bottom: 0;
    // border-bottom: none; // 国庆模块去掉底部横线
  }
}

/** 节日标题行 */
.block-label {
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.45rem;

  strong {
    font-size: 0.72rem;
    font-weight: 600;
  }

  span {
    font-size: 0.58rem;
    color: #999;
  }
}

/** 单选按钮组 */
.radio-row {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.radio-item {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.68rem;
  color: #333;
  cursor: pointer;

  input {
    width: 0.75rem;
    height: 0.75rem;
    accent-color: #176df9;
    flex-shrink: 0;
    /* 覆盖全局 -webkit-appearance: none 重置，恢复 radio 原生样式 */
    -webkit-appearance: auto;
    appearance: auto;
  }
}

/** 日期选择面板 */
.date-panel {
  margin-top: 0.45rem;
  padding-top: 0.45rem;
  border-top: 1px dashed #e5e7eb;
}

.date-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
}

/** 日期选项 */
.date-chip {
  min-width: 2.75rem;
  padding: 0.32rem 0.35rem;
  border: 1px solid #d9d9d9;
  border-radius: 0.35rem;
  background: #fff;
  font-size: 0.58rem;
  color: #666;
  text-align: center;
  cursor: pointer;
  user-select: none;
  line-height: 1.25;

  small {
    display: block;
    font-size: 0.5rem;
    color: #aaa;
  }

  /** 选中状态 */
  &.is-selected {
    border-color: #176df9;
    background: #e8f1fe;
    color: #176df9;
    font-weight: 600;

    small {
      color: #176df9;
    }
  }

  /** 禁用状态（全程休息） */
  &.is-disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
}

/** 已选日期摘要 */
.selected-summary {
  margin-top: 0.4rem;
  font-size: 0.58rem;
  color: #176df9;
  line-height: 1.45;

  /** 警告状态（未选择日期） */
  &.is-warn {
    color: #fa8c16;
  }
}

/** 底部按钮区 */
.holiday-foot {
  padding: 0.55rem 0.85rem 0.85rem;
}

.btn-primary {
  width: 100%;
  height: 2.1rem;
  border-radius: 0.45rem;
  font-size: 0.72rem;
  font-weight: 500;
}

/** 关闭按钮 */
.holiday-close {
  margin-top: 0.85rem;
  flex-shrink: 0;
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  border: 1.5px solid rgba(255, 255, 255, 0.85);
  background: rgba(0, 0, 0, 0.15);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;

  svg {
    width: 0.85rem;
    height: 0.85rem;
    display: block;
  }
}
</style>



// WEBPACK FOOTER //
// src/pages/home/components/holidayConfirm.vue